/**
 * Practice Performance Metrics Service
 * Tracks and analyzes practice-wide performance metrics
 */

import { supabase } from './supabase'

/**
 * Get practice performance metrics
 */
export const getPracticeMetrics = async (organizationId, timeRange = 30) => {
  try {
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - timeRange)

    // Get all metrics in parallel
    const [
      clientsData,
      examinationsData,
      carePlansData,
      therapiesData,
      sessionsData,
      outcomesData,
    ] = await Promise.all([
      // Client metrics
      supabase
        .from('clients')
        .select('id, created_at')
        .eq('organization_id', organizationId)
        .gte('created_at', startDate.toISOString()),

      // Examination metrics
      supabase
        .from('examinations')
        .select('id, created_at, client_id')
        .eq('organization_id', organizationId)
        .gte('created_at', startDate.toISOString()),

      // Care plan metrics
      supabase
        .from('care_plans')
        .select('id, status, start_date, end_date, created_at')
        .eq('organization_id', organizationId)
        .gte('created_at', startDate.toISOString()),

      // Therapy metrics
      supabase
        .from('therapies')
        .select('id, status, scheduled_date, completed_at')
        .eq('organization_id', organizationId)
        .gte('scheduled_date', startDate.toISOString()),

      // Session metrics
      supabase
        .from('sessions')
        .select('id, session_date, duration')
        .eq('organization_id', organizationId)
        .gte('session_date', startDate.toISOString()),

      // Outcome metrics
      supabase
        .from('client_outcomes')
        .select('id, outcome_type, outcome_value, baseline_value')
        .eq('organization_id', organizationId)
        .gte('measurement_date', startDate.toISOString()),
    ])

    const clients = clientsData.data || []
    const examinations = examinationsData.data || []
    const carePlans = carePlansData.data || []
    const therapies = therapiesData.data || []
    const sessions = sessionsData.data || []
    const outcomes = outcomesData.data || []

    // Calculate metrics
    const metrics = {
      // Client metrics
      newClients: clients.length,
      totalClients: (await supabase.from('clients').select('id', { count: 'exact', head: true }).eq('organization_id', organizationId)).count || 0,

      // Examination metrics
      examinationsCount: examinations.length,
      examinationsPerClient: clients.length > 0 ? (examinations.length / clients.length).toFixed(2) : 0,

      // Care plan metrics
      carePlansCount: carePlans.length,
      activeCarePlans: carePlans.filter((cp) => cp.status === 'active').length,
      completedCarePlans: carePlans.filter((cp) => cp.status === 'completed').length,
      carePlanCompletionRate:
        carePlans.length > 0
          ? Math.round((carePlans.filter((cp) => cp.status === 'completed').length / carePlans.length) * 100)
          : 0,

      // Therapy metrics
      therapiesScheduled: therapies.length,
      therapiesCompleted: therapies.filter((t) => t.status === 'completed').length,
      therapyCompletionRate:
        therapies.length > 0
          ? Math.round((therapies.filter((t) => t.status === 'completed').length / therapies.length) * 100)
          : 0,

      // Session metrics
      sessionsCount: sessions.length,
      totalSessionDuration: sessions.reduce((sum, s) => sum + (s.duration || 0), 0),
      averageSessionDuration: sessions.length > 0 ? Math.round(sessions.reduce((sum, s) => sum + (s.duration || 0), 0) / sessions.length) : 0,

      // Outcome metrics
      outcomesRecorded: outcomes.length,
      averageOutcomeImprovement: calculateAverageImprovement(outcomes),

      // Engagement metrics
      activeClients: new Set([...examinations, ...carePlans, ...therapies, ...sessions].map((item) => item.client_id || item.id)).size,
      clientRetentionRate: calculateRetentionRate(clients, examinations, carePlans),
    }

    return { success: true, data: metrics }
  } catch (error) {
    console.error('Error getting practice metrics:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Calculate average improvement from outcomes
 */
const calculateAverageImprovement = (outcomes) => {
  if (!outcomes || outcomes.length === 0) return 0

  let improvements = 0
  let comparisons = 0

  outcomes.forEach((outcome) => {
    if (outcome.baseline_value && outcome.outcome_value) {
      comparisons++
      const improvement = ((outcome.outcome_value - outcome.baseline_value) / outcome.baseline_value) * 100
      improvements += improvement
    }
  })

  return comparisons > 0 ? Math.round(improvements / comparisons) : 0
}

/**
 * Calculate client retention rate
 */
const calculateRetentionRate = (newClients, examinations, carePlans) => {
  if (newClients.length === 0) return 0

  // Clients who have had examinations, care plans, or therapies
  const activeClientIds = new Set([
    ...examinations.map((e) => e.client_id),
    ...carePlans.map((cp) => cp.client_id),
  ])

  const retainedClients = newClients.filter((client) => activeClientIds.has(client.id)).length

  return Math.round((retainedClients / newClients.length) * 100)
}

/**
 * Get performance trends over time
 */
export const getPerformanceTrends = async (organizationId, days = 90) => {
  try {
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - days)

    // Get daily metrics
    const dailyMetrics = []

    for (let i = 0; i < days; i++) {
      const date = new Date(startDate)
      date.setDate(date.getDate() + i)
      const nextDate = new Date(date)
      nextDate.setDate(nextDate.getDate() + 1)

      const [clients, examinations, sessions] = await Promise.all([
        supabase
          .from('clients')
          .select('id', { count: 'exact', head: true })
          .eq('organization_id', organizationId)
          .gte('created_at', date.toISOString())
          .lt('created_at', nextDate.toISOString()),

        supabase
          .from('examinations')
          .select('id', { count: 'exact', head: true })
          .eq('organization_id', organizationId)
          .gte('created_at', date.toISOString())
          .lt('created_at', nextDate.toISOString()),

        supabase
          .from('sessions')
          .select('id', { count: 'exact', head: true })
          .eq('organization_id', organizationId)
          .gte('session_date', date.toISOString())
          .lt('session_date', nextDate.toISOString()),
      ])

      dailyMetrics.push({
        date: date.toISOString().split('T')[0],
        clients: clients.count || 0,
        examinations: examinations.count || 0,
        sessions: sessions.count || 0,
      })
    }

    return { success: true, data: dailyMetrics }
  } catch (error) {
    console.error('Error getting performance trends:', error)
    return { success: false, error: error.message }
  }
}

export default {
  getPracticeMetrics,
  getPerformanceTrends,
}

